import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { addMemory, getMeetOrbitCard } from "@/lib/relationships.functions";
import { confirmMatch } from "@/lib/auto-discover.functions";
import { VoiceMemoButton } from "@/components/voice-memo-button";
import { Avatar } from "@/components/avatar";
import {
  Sparkles, ChevronLeft, ChevronRight, Pencil, Check, Loader2,
  Linkedin, Globe, Twitter, HelpCircle, X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type Person = { id: string; name: string };

export function MeetYourOrbit({ contactIds, contactNames }: { contactIds: string[]; contactNames: string[] }) {
  const people: Person[] = contactIds.map((id, i) => ({ id, name: contactNames[i] ?? "" }));
  const [idx, setIdx] = useState(0);
  if (people.length === 0) {
    return <p className="text-sm text-muted-foreground">No people to show — you can always add some later.</p>;
  }
  const person = people[idx];
  const last = idx === people.length - 1;

  return (
    <div className="space-y-4">
      <PersonMagicCard key={person.id} person={person} />

      {people.length > 1 && (
        <div className="flex items-center justify-center gap-3 pt-1">
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="grid h-8 w-8 place-items-center rounded-full glass-soft text-muted-foreground disabled:opacity-30 hover:text-foreground"
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {people.map((_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === idx ? "w-5 bg-[var(--accent-neon)]" : "w-1.5 bg-white/15",
                )}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIdx((i) => Math.min(people.length - 1, i + 1))}
            disabled={last}
            className="grid h-8 w-8 place-items-center rounded-full glass-soft text-muted-foreground disabled:opacity-30 hover:text-foreground"
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function LinkIcon({ kind }: { kind: string }) {
  const cls = "h-3.5 w-3.5";
  if (kind === "linkedin") return <Linkedin className={cls} />;
  if (kind === "twitter") return <Twitter className={cls} />;
  return <Globe className={cls} />;
}

function PersonMagicCard({ person }: { person: Person }) {
  const fetchCard = useServerFn(getMeetOrbitCard);
  const qc = useQueryClient();
  const [pollUntil] = useState(() => Date.now() + 22_000);
  const { data } = useQuery({
    queryKey: ["meet-orbit-card", person.id],
    queryFn: () => fetchCard({ data: { contactId: person.id } }),
    refetchInterval: () => (Date.now() < pollUntil ? 2_000 : false),
    refetchOnWindowFocus: false,
  });

  const c = data?.contact;
  const ctx = data?.context;
  const links = data?.links ?? [];
  const cands = data?.candidates ?? null;
  const confidence = data?.confidence ?? null;
  const needsConfirm = !!cands && cands.length >= 2 && (confidence ?? 1) < 0.8;

  const headline = (() => {
    if (!c) return "";
    const role = c.title && c.company ? `${c.title} at ${c.company}` : c.title || c.company || "";
    return [role, c.location].filter(Boolean).join(" · ");
  })();

  const toStrings = (arr: unknown): string[] =>
    (Array.isArray(arr) ? arr : [])
      .map((x: any) =>
        typeof x === "string"
          ? x
          : x && typeof x === "object"
            ? String(x.title ?? x.text ?? x.name ?? "")
            : "",
      )
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  const projects = toStrings(ctx?.active_projects).slice(0, 2);
  const milestones = toStrings(ctx?.recent_milestones).slice(0, 2 - projects.length);
  const workingOn = [...projects, ...milestones].slice(0, 2);
  const passions = toStrings(ctx?.passions).slice(0, 3);
  const familyBits: string[] = [];
  if (ctx?.partner_name) familyBits.push(`Partner: ${ctx.partner_name}`);
  if (ctx?.kids?.length) familyBits.push(`${ctx.kids.length} ${ctx.kids.length === 1 ? "kid" : "kids"}`);
  if (ctx?.pets?.length) familyBits.push(`${ctx.pets.length} ${ctx.pets.length === 1 ? "pet" : "pets"}`);

  const stillSearching =
    Date.now() < pollUntil &&
    !headline &&
    workingOn.length === 0 &&
    passions.length === 0 &&
    familyBits.length === 0 &&
    !needsConfirm;

  const firstName = (person.name.trim().split(" ")[0] || "them").trim();
  const [savedFacts, setSavedFacts] = useState<string[]>([]);
  const onCaptured = (facts: string[]) => {
    setSavedFacts((prev) => [...prev, ...facts.filter((f) => !prev.includes(f))]);
    void qc.invalidateQueries({ queryKey: ["meet-orbit-card", person.id] });
  };

  return (
    <div className="rounded-2xl glass-soft p-5 space-y-4 animate-in fade-in slide-in-from-bottom-2">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="relative">
          {stillSearching && (
            <span className="absolute -inset-1 rounded-full bg-primary/30 blur-md animate-pulse" />
          )}
          <Avatar name={person.name} photoUrl={c?.photo_url} size={56} className="relative" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-medium truncate">{person.name}</p>
          {headline && (
            <p className="text-[11px] text-muted-foreground truncate animate-in fade-in">{headline}</p>
          )}
        </div>
        {links.length > 0 && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            {links.slice(0, 3).map((l) => (
              <a
                key={l.url}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="grid h-6 w-6 place-items-center rounded-full glass-soft hover:text-foreground"
                aria-label={l.kind}
              >
                <LinkIcon kind={l.kind} />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Candidate clarifier */}
      {needsConfirm && cands && (
        <CandidatePicker contactId={person.id} candidates={cands} confidence={confidence ?? 0} />
      )}

      {/* What Pyng found */}
      {(stillSearching || workingOn.length > 0 || passions.length > 0 || familyBits.length > 0) && (
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            <Sparkles className="h-3 w-3 text-primary" />
            {stillSearching ? "Pyng is looking…" : "What Pyng found"}
          </p>
          {stillSearching ? (
            <div className="space-y-1.5">
              <div className="h-2.5 w-3/4 rounded bg-white/5 animate-pulse" />
              <div className="h-2.5 w-1/2 rounded bg-white/5 animate-pulse" />
              <div className="h-2.5 w-2/3 rounded bg-white/5 animate-pulse" />
            </div>
          ) : (
            <div className="space-y-2.5">
              {workingOn.length > 0 && (
                <SignalRow label="Working on" items={workingOn} delay={0} />
              )}
              {passions.length > 0 && (
                <SignalRow label="Cares about" items={passions} delay={120} />
              )}
              {familyBits.length > 0 && (
                <div
                  className="text-[11px] text-muted-foreground animate-in fade-in slide-in-from-bottom-1"
                  style={{ animationDelay: "240ms" }}
                >
                  <span className="text-muted-foreground/70">Around the edges · </span>
                  {familyBits.join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Capture */}
      <div className="rounded-2xl bg-black/20 ring-1 ring-white/5 p-4 flex flex-col items-center gap-2">
        <p className="text-sm text-foreground/90 text-center">
          Add one quick note about <span className="font-medium">{firstName}</span>
        </p>
        <p className="text-[11px] text-muted-foreground text-center">
          How you met, what they care about, what they're building.
        </p>
        <div className="pt-1">
          <VoiceMemoButton contactId={person.id} onSuccess={({ facts }) => onCaptured(facts)} />
        </div>
        <TypeInsteadInput contactId={person.id} onSaved={(f) => onCaptured([f])} />
      </div>

      {savedFacts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {savedFacts.map((f, i) => (
            <span
              key={`${i}-${f}`}
              className="rounded-full glass-soft px-3 py-1 text-[11px] text-foreground animate-in fade-in slide-in-from-bottom-1"
            >
              {f}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SignalRow({ label, items, delay }: { label: string; items: string[]; delay: number }) {
  return (
    <div className="animate-in fade-in slide-in-from-bottom-1" style={{ animationDelay: `${delay}ms` }}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span
            key={`${i}-${it}`}
            className="rounded-full bg-primary/10 text-primary ring-1 ring-primary/20 px-2.5 py-1 text-[11px]"
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

function CandidatePicker({
  contactId,
  candidates,
  confidence,
}: {
  contactId: string;
  candidates: Array<{ kind: string; url: string; title?: string; why?: string }>;
  confidence: number;
}) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const confirm = useServerFn(confirmMatch);
  const mut = useMutation({
    mutationFn: (accept: boolean) => confirm({ data: { contactId, accept } }),
    onSuccess: (res: any) => {
      if (res?.status === "applied") toast.success("Got it — Pyng will refresh their context.");
      void qc.invalidateQueries({ queryKey: ["meet-orbit-card", contactId] });
      setOpen(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't save"),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 ring-1 ring-amber-500/30 text-amber-300 px-2.5 py-1 text-[11px] hover:bg-amber-500/15"
      >
        <HelpCircle className="h-3 w-3" /> Not quite right? ({Math.round(confidence * 100)}% match)
      </button>
    );
  }

  return (
    <div className="rounded-xl glass-soft p-3 space-y-2 animate-in fade-in slide-in-from-top-1">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">Is this who you meant?</p>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="grid h-5 w-5 place-items-center rounded-full text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      <ul className="space-y-1.5">
        {candidates.slice(0, 3).map((c) => {
          const host = (() => {
            try { return new URL(c.url).hostname.replace(/^www\./, ""); } catch { return c.url; }
          })();
          return (
            <li key={c.url} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/[0.03]">
              <LinkIcon kind={c.kind} />
              <a
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 text-[11px] text-foreground/85 hover:text-foreground truncate"
                title={c.title ?? c.url}
              >
                {c.title ?? host}
                <span className="ml-1 text-muted-foreground">· {host}</span>
              </a>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={() => !mut.isPending && mut.mutate(false)}
          disabled={mut.isPending}
          className="text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-40"
        >
          None of these
        </button>
        <button
          type="button"
          onClick={() => !mut.isPending && mut.mutate(true)}
          disabled={mut.isPending}
          className="inline-flex h-7 items-center gap-1.5 rounded-full bg-primary px-3 text-[11px] font-medium text-primary-foreground disabled:opacity-50"
        >
          {mut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          That's them
        </button>
      </div>
    </div>
  );
}

function TypeInsteadInput({ contactId, onSaved }: { contactId: string; onSaved: (fact: string) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = useServerFn(addMemory);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        <Pencil className="h-3 w-3" /> Type instead
      </button>
    );
  }

  const submit = async () => {
    const v = text.trim();
    if (v.length < 2) return;
    setBusy(true);
    try {
      await save({ data: { contactId, text: v } });
      onSaved(v);
      setText("");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full flex items-center gap-2">
      <input
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void submit();
          if (e.key === "Escape") setOpen(false);
        }}
        placeholder="A fact, a story, anything…"
        className="flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground/50 border-b border-white/10 focus:border-primary pb-1 transition-colors"
      />
      <button
        type="button"
        onClick={() => void submit()}
        disabled={busy || text.trim().length < 2}
        className="grid h-7 w-7 place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-40"
        aria-label="Save"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
